import {Button, Dropdown, Form, Header, Input, Menu, Sidebar} from "semantic-ui-react";
import {useAppDispatch} from "../../app/hooks";
import {useSelector} from "react-redux";
import {RootState} from "../../app/store";
import {BARCODE, PICTURE, VARIABLE_PICTURE, SIMPLE_TEXT, VARIABLE_TEXT, VERTICAL_LINE, HORIZONTAL_LINE} from "../../utils/ItemTypes";
import {ChangeLabelParams, ChangeObjectWithStyle, ChangeValue, ChangeValueNoHistory, RemoveAllSelection} from "../MainActions";
import {calcPxInMM, orientation, round} from "../../utils/constants";
import {FontModal} from "../FontModal/FontModal";
import {useState, useRef} from "react";
import {SimpleTextForm} from "./SimpleTextForm";
import {VariableTextForm} from "./VariableTextForm";
import {BarcodeForm} from "./BarcodeForm";
import {PictureForm} from "./PictureForm";
import {LineForm} from "./LineForm";
import {IItem} from "../../interfaces/Item";
import {ChangeBarcodeValNoHist, GenerateBarcode} from "../BarcodeActions";
import {IBarcode} from "../../interfaces/Barcode";
import {C128} from "../../utils/barcodeUtils";
import {ActionCreators as UndoActionCreators} from "redux-undo";
import {IDpi} from "../../interfaces/Dpi";
import {ILabelSize} from "../../interfaces/ILabelSize";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { showErrorToast } from "../../utils/showToast";
import { TagModal } from "../CompanyDataTables/TagModal";
import { DpiModal } from "../CompanyDataTables/DpiModal";
import { LabelSizeModal } from "../CompanyDataTables/LabelSizeModal";
interface Props {
  setZoom: (zoom: number) => void;
}
export const PropsSidebar = ({setZoom}: Props) => {
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const {role} = useSelector((state: RootState) => state.auth);
  const [showSidebar, setShowSidebar] = useState(true);
  const [openFontModal, setOpenFontModal] = useState(false);
  const {labelParams, labelItems} = useSelector((state: RootState) => state.labelEdit.present);
  const {labelSizes, dpiValues} = useSelector((state: RootState) => state.refData);
  const selectedItem = useSelector((state: RootState) => state.labelEdit.present.labelItems.find((item: IItem) => item.selected));
  const pxInMM = labelParams.pxInMM;
  const barcodeRegenerationTimerRef = useRef<NodeJS.Timeout | null>(null);

  const labelSizesOptions = labelSizes.map((item: ILabelSize) => ({key: item.id, text: item.name || item.id, value: item.id}));
  const dpiOptions = dpiValues.map((item: IDpi) => ({key: item.dpi, text: item.dpi, value: item.dpi}));
  const orientations = [
    { key: orientation.portrait, text: t("portrait") /*orientation.portrait*/, value: orientation.portrait },
    { key: orientation.landscape, text: t("landscape") /*orientation.landscape*/, value: orientation.landscape },
  ];

  const [openTagModal, setOpenTagModal] = useState({open: false, row: {}, updateRow: false});
  const [openDpiModal, setOpenDpiModal] = useState(false);
  const [openLabelSizeModal, setOpenLabelSizeModal] = useState({open: false, row: {}, updateRow: false});

  const handleAddNewRecord = (gridName: string) => {
    switch (gridName) {
      case "tags":
        setOpenTagModal({open: true, row: {}, updateRow: false});
        break;
      case "dpi":
        setOpenDpiModal(true);
        break;
      case "labelSize":
        setOpenLabelSizeModal({open: true, row: {}, updateRow: false});
        break;
      default:
        break;
    }
  };

  const onChange = (propName: string, propValue: any) => {
    if (selectedItem !== undefined && selectedItem !== null) {
      setZoom(0);
      dispatch(ChangeValue(selectedItem, propName, propValue));
    }
  };
  const onChangeNoHistory = (propName: string, propValue: any) => {
    if (selectedItem !== undefined && selectedItem !== null) {
      setZoom(0);
      dispatch(ChangeValueNoHistory(selectedItem, propName, propValue));
    }
  };

  const onChangeWithStyle = (propName: string, propValue: any, propNameStyle: string, propValueStyle: string) => {
    if (selectedItem !== undefined && selectedItem !== null) {
      setZoom(0);
      dispatch(ChangeObjectWithStyle(selectedItem, propName, propValue, propNameStyle, propValueStyle));
    }
  };

  const onLabelChange = (propName: string, propValue: any) => {
    setZoom(0);
    dispatch(ChangeLabelParams(propName, propValue));
    if ((propName === "dpi" || propName === "showDefaultValues") && labelItems.length > 0) {
      // debounce barcode regeneration to prevent excessive memory usage
      if (barcodeRegenerationTimerRef.current) {
        clearTimeout(barcodeRegenerationTimerRef.current);
      }
      barcodeRegenerationTimerRef.current = setTimeout(() => {
        labelItems.forEach((item: IItem) => {
          if (item.type === BARCODE) {
            let barcode: IBarcode = {
              X: item?.xInMM || 0,
              Y: item?.yInMM || 0,
              Name: item?.name || "",
              Width: item?.width || 0,
              Height: item?.height || 0,
              ShowCaption: Number(item?.showCaption) || 0,
              CaptionSize: item?.captionSize || 0,
              Rotation: item?.rotation || 0,
              Type: item?.barcodeType || C128,
              DefaultText: item?.defaultText || "",
            };
            dispatch(GenerateBarcode(item.itemid, barcode)).then((barcodeUrl: any) => {
              if (barcodeUrl && item !== undefined && item !== null) {
                dispatch(ChangeBarcodeValNoHist(item, "value", barcodeUrl));
              }
            });
          }
        });
      }, 500);
    }
    dispatch(UndoActionCreators.clearHistory());
  };

  const onLabelSizeChange = (propValue: any) => {
    // const [width, height] = propValue.split("x");
    const size = labelSizes.find((item: ILabelSize) => item.id === propValue);
    if (size) {
      onLabelChange("selectedLabelSizeId", size.id);
      onLabelChange("sizeInMM", size.width + "x" + size.height);
      if (labelParams.orientation === orientation.landscape) {
        onLabelChange("widthInPx", Number(size.height) * pxInMM);
        onLabelChange("heightInPx", Number(size.width) * pxInMM);
      } else {
        onLabelChange("widthInPx", Number(size.width) * pxInMM);
        onLabelChange("heightInPx", Number(size.height) * pxInMM);
      }
    }
  };

  const onOrientationChange = (propValue: any) => {
    // const [width, height] = labelParams.sizeInMM.split("x");
    const selectedLabelSizeId = labelParams.selectedLabelSizeId;
    const size = labelSizes.find((item: ILabelSize) => item.id === selectedLabelSizeId);
    if (!size) {showErrorToast(t("error"), t("select_label_size")); return;}
    onLabelChange("orientation", propValue);

    if (propValue === orientation.landscape) {
      onLabelChange("widthInPx", Number(size.height) * pxInMM);
      onLabelChange("heightInPx", Number(size.width) * pxInMM);
    } else {
      onLabelChange("widthInPx", Number(size.width) * pxInMM);
      onLabelChange("heightInPx", Number(size.height) * pxInMM);
    }
  };

  const onDpiChange = (propValue: any) => {
    //const [width, height] = labelParams.sizeInMM.split("x");
    const selectedLabelSizeId = labelParams.selectedLabelSizeId;
    const size = labelSizes.find((item: ILabelSize) => item.id === selectedLabelSizeId);
    if (!size) {showErrorToast(t("error"), t("select_label_size")); return;}
    onLabelChange("dpi", propValue);
    const newPxInMM = calcPxInMM(propValue);
    onLabelChange("pxInMM", newPxInMM);
    if (labelParams.orientation === orientation.landscape) {
      onLabelChange("widthInPx", Number(size.height) * newPxInMM);
      onLabelChange("heightInPx", Number(size.width) * newPxInMM);
    } else {
      onLabelChange("widthInPx", Number(size.width) * newPxInMM);
      onLabelChange("heightInPx", Number(size.height) * newPxInMM);
    }
  };

  const onSettingClick = () => {
    setZoom(0);
    dispatch(RemoveAllSelection()).then(() => {
      if (!showSidebar) setShowSidebar(true);
    });
  };

  const renderProps = () => {
    switch (selectedItem?.type) {
      case SIMPLE_TEXT:
        return <SimpleTextForm style={style} selectedItem={selectedItem} pxInMM={pxInMM} setOpenFontModal={setOpenFontModal} onChangeWithStyle={onChangeWithStyle} onChange={onChange} />;
      case VARIABLE_TEXT:
        return <VariableTextForm role={role} handleAddNewRecord={() => handleAddNewRecord("tags")} style={style} selectedItem={selectedItem} pxInMM={pxInMM} setOpenFontModal={setOpenFontModal} onChangeWithStyle={onChangeWithStyle} onChange={onChange} />;
      case BARCODE:
        return <BarcodeForm role={role} handleAddNewRecord={() => handleAddNewRecord("tags")} style={style} selectedItem={selectedItem} pxInMM={pxInMM} onChangeWithStyle={onChangeWithStyle} onChange={onChange} onChangeNoHistory={onChangeNoHistory} />;
      case PICTURE:
      case VARIABLE_PICTURE:
        return <PictureForm role={role} handleAddNewRecord={() => handleAddNewRecord("tags")} style={style} selectedItem={selectedItem} pxInMM={pxInMM} onChangeWithStyle={onChangeWithStyle} onChange={onChange} />;
      case VERTICAL_LINE:
      case HORIZONTAL_LINE:
        return <LineForm style={style} selectedItem={selectedItem} pxInMM={pxInMM} onChangeWithStyle={onChangeWithStyle} onChange={onChange} onChangeNoHistory={onChangeNoHistory} />;
      default: {
        //label
        return (
          <Form style={style.form}>
            <Form.Field inline style={style.field}>
              <label>{t("size")}:</label>
              <div style={{display: "flex", alignItems: "center", width: "100%"}}>
                <Dropdown
                  size="small"
                  options={labelSizesOptions}
                  value={labelParams.selectedLabelSizeId}
                  fluid
                  selection={true}
                  search={(options, query) => 
                    options.filter((option) => 
                      option.text?.toString().toLowerCase().includes(query.toLowerCase()) || 
                      option.value?.toString().toLowerCase().includes(query.toLowerCase())
                    )
                  }
                  onChange={(e, data) => {
                    onLabelSizeChange(data.value);
                  }}
                />
                {(role === "admin" || role === "super_admin") && <Button onClick={()=>handleAddNewRecord("labelSize")} className="icon" icon="plus" basic color="blue" style={{marginLeft: 4}} />}
              </div>
            </Form.Field>
            <Form.Field inline style={style.field}>
              <label>{t("orientation")}:</label>
              <div style={{display: "flex", alignItems: "center", width: "100%"}}>
                <Dropdown
                  size="small"
                  options={orientations}
                  value={labelParams.orientation}
                  fluid
                  selection={true}
                  search={(options, query) => 
                    options.filter((option) => 
                      option.text?.toString().toLowerCase().includes(query.toLowerCase()) || 
                      option.value?.toString().toLowerCase().includes(query.toLowerCase())
                    )
                  }
                  onChange={(e, data) => {
                    onOrientationChange(data.value);
                  }}
                />
              </div>
            </Form.Field>
            <Form.Field inline style={style.field}>
              <label>{t("dpi_label")}:</label>
              <div style={{display: "flex", alignItems: "center", width: "100%"}}>
              <Dropdown
                size="small"
                options={dpiOptions}
                value={labelParams.dpi}
                fluid
                selection={true}
                search={(options, query) => 
                  options.filter((option) => 
                    option.text?.toString().toLowerCase().includes(query.toLowerCase()) || 
                    option.value?.toString().toLowerCase().includes(query.toLowerCase())
                  )
                }
                onChange={(e, data) => {
                  onDpiChange(data.value);
                }}
              />
              {(role === "admin" || role === "super_admin") && <Button onClick={()=>handleAddNewRecord("dpi")} className="icon" icon="plus" basic color="blue" style={{marginLeft: 4}} />}
              </div>
            </Form.Field>
            {/* <Form.Field inline style={style.field}>
              <label>{t("increase")}:</label>
              <Input fluid size="small" style={{width: "100%"}} type="number" value={1} readOnly />
            </Form.Field> */}
            <Form.Field inline style={style.field}>
              <label>{t("width_px")}:</label>
              <Input fluid size="small" style={{width: "100%"}} type="number" value={Math.round(labelParams.widthInPx)} readOnly />
            </Form.Field>
            <Form.Field inline style={style.field}>
              <label>{t("height_px")}:</label>
              <Input fluid size="small" style={{width: "100%"}} type="number" value={Math.round(labelParams.heightInPx)} readOnly />
            </Form.Field>
            <Form.Field inline style={style.field}>
              <label>{t("dots_per_mm")}:</label>
              <Input fluid size="small" style={{width: "100%"}} type="number" value={round(labelParams.pxInMM)} readOnly />
            </Form.Field>
            <Form.Field inline style={style.field}>
              <label>{t("show_default_values")}:</label>
              <Form.Checkbox checked={labelParams.showDefaultValues} onChange={(e, data) => onLabelChange("showDefaultValues", data.checked)} />
            </Form.Field>
            <Form.Field>
              <label style={{textAlign: "left"}}>{t("default_picture_path")}:</label>
              <Input fluid size="small" style={{width: "100%"}} value={labelParams.defaultPath} onChange={(e) => onLabelChange("defaultPath", e.target.value)} />
            </Form.Field>
            <Form.Field inline style={style.field}>
              <label>{t("show_grid")}:</label>
              <Form.Checkbox
                checked={labelParams.showGrid}
                onChange={(e, data) => {
                  onLabelChange("showGrid", data.checked);
                  if (data.checked && !labelParams.gridCellWidth) onLabelChange("gridCellWidth", 10);
                }}
              />
            </Form.Field>
            <Form.Field inline style={style.field}>
              <label>{t("grid_size")}:</label>
              <Input fluid size="small" style={{width: "100%"}} min={0} disabled={!labelParams.showGrid} type="number" value={labelParams.gridCellWidth} onChange={(e, data) => onLabelChange("gridCellWidth", Math.round(Number(e.target.value)))} />
            </Form.Field>
          </Form>
        );
      }
    }
  };
  return (
    <>
      <FontModal selectedItem={selectedItem} onChange={onChange} open={openFontModal} onClose={() => setOpenFontModal(false)} />
      {(role === "admin" || role === "super_admin") && <>
        <TagModal open={openTagModal.open} row={openTagModal.row} updateRow={openTagModal.updateRow} onClose={() => setOpenTagModal({open: false, row: {}, updateRow: false})} />
        <DpiModal open={openDpiModal} onClose={() => setOpenDpiModal(false)} />
        <LabelSizeModal
          open={openLabelSizeModal.open}
          row={openLabelSizeModal.row}
          updateRow={openLabelSizeModal.updateRow}
          onClose={() => setOpenLabelSizeModal({open: false, row: {}, updateRow: false})}
        />
      </>}
      <Button
        circular
        compact
        icon={showSidebar ? "angle double right" : "angle double left"}
        onClick={() => setShowSidebar(!showSidebar)}
        style={{position: "absolute", top: 5, right: showSidebar ? 240 : 0, zIndex: 9999, backgroundColor: "#ffffff", color: "black", border: "1px solid #e7e7e8", boxShadow: "0 0 7px rgba(34, 36, 38, 0.35)"}}
      />
      <Button
        circular
        compact
        icon="setting"
        onClick={onSettingClick}
        style={{position: "absolute", top: 5, right: showSidebar ? 275 : 35, zIndex: 9999, backgroundColor: "#ffffff", color: "black", border: "1px solid #e7e7e8", boxShadow: "0 0 7px rgba(34, 36, 38, 0.35)"}}
      />
      <Sidebar as={Menu} direction="right" vertical animation="overlay" visible={showSidebar} style={style.sidebar}>
        <Header as="h3">{renderSidebarHeader(selectedItem?.type || "")}</Header>
        {renderProps()}
      </Sidebar>
    </>
  );
};

const renderSidebarHeader = (type: string) => {
  switch (type) {
    case SIMPLE_TEXT:
      return i18n.t("simple_text");// "Tekstas";
    case VARIABLE_TEXT:
      return i18n.t("variable_text");//"Kintamas tekstas";
    case BARCODE:
      return i18n.t("barcode");//"Barkodas";
    case PICTURE:
      return i18n.t("picture");//"Paveikslėlis";
    case VARIABLE_PICTURE:
      return i18n.t("variable_picture");//"Kintamas paveikslėlis";
    case VERTICAL_LINE:
      return i18n.t("vertical_line");//"Vertikali linija";
    case HORIZONTAL_LINE:
      return i18n.t("line");//"Linija";
    default:
      return i18n.t("label_settings");//"Puslapio nustatymai";
  }
};

const style = {
  sidebar: {
    paddingLeft: 1,
    paddingTop: 10,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  form: {padding: "20px 8px 20px 15px"},
  field: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
};
